import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../../lib/supabase';
import * as LuIcons from 'react-icons/lu';
import SafeIcon from '../../common/SafeIcon';

const { LuVideo, LuPlus, LuTrash2, LuX, LuPlayCircle, LuImage, LuAlignLeft, LuEdit, LuUpload, LuSave, LuSettings, LuMessageSquareHeart } = LuIcons;

const REVIEW_TYPES = [
  { id: 'video', label: 'فيديو', icon: LuVideo },
  { id: 'text', label: 'نص (اقتباس)', icon: LuAlignLeft },
  { id: 'image', label: 'صورة', icon: LuImage }
];

const AdminReviews = () => {
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);

  const avatarRef = useRef(null);
  const imageRef = useRef(null);

  const initialForm = {
    customer_name: '',
    car_purchased: '',
    review_type: 'video',
    video_url: '',
    content: '',
    avatar_url: '',
    section_title: 'آراء زبائننا',
    location: 'home_above_cars',
    is_active: true
  };

  const [formData, setFormData] = useState(initialForm);

  useEffect(() => {
    fetchReviews();
  }, []);

  const fetchReviews = async () => {
    setLoading(true);
    const { data } = await supabase.from('customer_reviews').select('*').order('created_at', { ascending: false });
    if (data) setReviews(data);
    setLoading(false);
  };

  const handleUpload = async (e, type) => {
    const file = e.target.files?.[0];
    if (!file) return;

    type === 'avatar' ? setUploadingAvatar(true) : setUploadingImage(true);

    try {
      const ext = file.name.split('.').pop();
      const fileName = `review_${type}_${Date.now()}.${ext}`;
      const { error } = await supabase.storage.from('images').upload(fileName, file);
      if (error) throw error;

      const url = supabase.storage.from('images').getPublicUrl(fileName).data.publicUrl;

      if (type === 'avatar') {
        setFormData(prev => ({ ...prev, avatar_url: url }));
      } else {
        setFormData(prev => ({ ...prev, content: url }));
      }
    } catch (err) {
      alert('خطأ في الرفع: ' + err.message);
    } finally {
      type === 'avatar' ? setUploadingAvatar(false) : setUploadingImage(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (editingId) {
      const { error } = await supabase.from('customer_reviews').update(formData).eq('id', editingId);
      if (error) alert(error.message); else resetForm();
    } else {
      const { error } = await supabase.from('customer_reviews').insert([formData]);
      if (error) alert(error.message); else resetForm();
    }
  };

  const resetForm = () => {
    setIsAdding(false);
    setEditingId(null);
    setFormData(initialForm);
    fetchReviews();
  };

  const handleDelete = async (id) => {
    if (window.confirm('هل أنت متأكد من حذف هذه المراجعة؟')) {
      await supabase.from('customer_reviews').delete().eq('id', id);
      fetchReviews();
    }
  };

  const toggleStatus = async (review) => {
    await supabase.from('customer_reviews').update({ is_active: !review.is_active }).eq('id', review.id);
    fetchReviews();
  };

  const startEdit = (review) => {
    setFormData({
      customer_name: review.customer_name || '',
      car_purchased: review.car_purchased || '',
      review_type: review.review_type || 'video',
      video_url: review.video_url || '',
      content: review.content || '',
      avatar_url: review.avatar_url || '',
      section_title: review.section_title || 'آراء زبائننا',
      location: review.location || 'home_above_cars',
      is_active: review.is_active
    });
    setEditingId(review.id);
    setIsAdding(true);
  };

  return (
    <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-6" dir="rtl">
      
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-bold text-slate-800 flex items-center">
          <SafeIcon icon={LuMessageSquareHeart} className="w-6 h-6 me-2 text-indigo-600" />
          إدارة مراجعات ومجموعات الزبائن
        </h2>
        {!isAdding && (
          <button onClick={() => setIsAdding(true)} className="bg-indigo-600 hover:bg-indigo-700 text-white px-4 py-2 rounded-xl font-medium transition-colors flex items-center gap-2">
            <SafeIcon icon={LuPlus} className="w-5 h-5" />
            إضافة مراجعة
          </button>
        )}
      </div>

      {isAdding && (
        <form onSubmit={handleSubmit} className="mb-8 bg-slate-50 p-5 rounded-2xl border border-slate-200">
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-bold text-slate-800">{editingId ? 'تعديل المراجعة' : 'إضافة مراجعة جديدة'}</h3>
            <button type="button" onClick={resetForm} className="p-1.5 text-slate-400 hover:text-slate-600 bg-slate-200 rounded-lg">
              <SafeIcon icon={LuX} className="w-4 h-4" />
            </button>
          </div>

          {/* إعدادات المجموعة (Title & Location) */}
          <div className="bg-white p-4 rounded-xl border border-indigo-100 mb-6 shadow-sm">
            <h4 className="text-sm font-black text-indigo-900 mb-3 flex items-center gap-2">
              <SafeIcon icon={LuSettings} className="w-4 h-4" />
              إعدادات مجموعة العرض
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold text-slate-600 mb-1">عنوان المجموعة التابعة لها</label>
                <input required type="text" value={formData.section_title} onChange={e => setFormData({ ...formData, section_title: e.target.value })} className="w-full p-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none" placeholder="مثلاً: آراء زبائننا، تسليم السيارات..." />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-600 mb-1">مكان الظهور في الرئيسية</label>
                <select value={formData.location} onChange={e => setFormData({ ...formData, location: e.target.value })} className="w-full p-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none bg-white">
                  <option value="home_above_cars">فوق قسم السيارات (تحت الإحصائيات)</option>
                  <option value="home_bottom">في أسفل الموقع</option>
                  <option value="none">إخفاء من الرئيسية</option>
                </select>
              </div>
            </div>
            <p className="text-[11px] text-slate-500 mt-2">ملاحظة: سيتم تجميع المراجعات التي تشترك في نفس "العنوان" و "المكان" في شريط واحد تلقائياً.</p>
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-slate-700 mb-2">نوع المراجعة</label>
            <div className="flex gap-3 flex-wrap sm:flex-nowrap">
              {REVIEW_TYPES.map(type => (
                <button
                  key={type.id}
                  type="button"
                  onClick={() => setFormData({ ...formData, review_type: type.id })}
                  className={`flex-1 min-w-[100px] flex flex-col items-center justify-center p-3 rounded-xl border-2 transition-colors ${formData.review_type === type.id ? 'border-indigo-600 bg-indigo-50 text-indigo-700' : 'border-slate-200 bg-white text-slate-600 hover:bg-slate-50'}`}
                >
                  <SafeIcon icon={type.icon} className="w-6 h-6 mb-1" />
                  <span className="text-xs font-bold">{type.label}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">اسم الزبون</label>
              <input required type="text" value={formData.customer_name} onChange={e => setFormData({ ...formData, customer_name: e.target.value })} className="w-full p-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">السيارة المشتراة (اختياري)</label>
              <input type="text" value={formData.car_purchased} onChange={e => setFormData({ ...formData, car_purchased: e.target.value })} className="w-full p-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none" placeholder="مثال: VW Golf 8" />
            </div>

            {formData.review_type === 'video' && (
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-slate-700 mb-1">رابط الفيديو (يوتيوب، يوتيوب شورت، تيك توك، أو MP4)</label>
                <input required type="url" value={formData.video_url} onChange={e => setFormData({ ...formData, video_url: e.target.value })} dir="ltr" className="w-full p-2.5 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none text-left" placeholder="https://..." />
              </div>
            )}

            {formData.review_type === 'text' && (
              <>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-slate-700 mb-1">نص المراجعة</label>
                  <textarea required value={formData.content} onChange={e => setFormData({ ...formData, content: e.target.value })} rows="3" className="w-full p-3 border border-slate-200 rounded-lg focus:ring-2 focus:ring-indigo-500 outline-none" placeholder="ماذا قال الزبون عن خدمتنا؟"></textarea>
                </div>
                <div className="md:col-span-2 bg-white p-4 rounded-xl border border-slate-200">
                  <label className="block text-sm font-bold text-slate-700 mb-3">صورة الزبون (أفاتار - اختياري)</label>
                  <div className="flex items-center gap-4">
                    <div className="w-16 h-16 bg-slate-100 rounded-full border border-slate-300 flex items-center justify-center overflow-hidden flex-shrink-0">
                      {formData.avatar_url ? (
                        <img src={formData.avatar_url} alt="avatar" className="w-full h-full object-cover" />
                      ) : (
                        <SafeIcon icon={LuImage} className="w-6 h-6 text-slate-300" />
                      )}
                    </div>
                    <div className="flex-1 space-y-2">
                      <input type="file" accept="image/*" ref={avatarRef} onChange={(e) => handleUpload(e, 'avatar')} className="hidden" />
                      <button type="button" onClick={() => avatarRef.current?.click()} disabled={uploadingAvatar} className="flex items-center gap-2 px-4 py-2 bg-slate-100 text-slate-700 hover:bg-slate-200 rounded-lg text-sm font-bold transition-colors">
                        <SafeIcon icon={uploadingAvatar ? LuX : LuUpload} className="w-4 h-4" />
                        {uploadingAvatar ? 'جاري الرفع...' : 'رفع صورة'}
                      </button>
                      <input type="url" value={formData.avatar_url} onChange={e => setFormData({ ...formData, avatar_url: e.target.value })} placeholder="أو ضع رابط الصورة هنا" dir="ltr" className="w-full p-2 border border-slate-200 rounded-lg text-xs outline-none text-left" />
                    </div>
                  </div>
                </div>
              </>
            )}

            {formData.review_type === 'image' && (
              <div className="md:col-span-2 bg-white p-4 rounded-xl border border-slate-200">
                <label className="block text-sm font-bold text-slate-700 mb-3">صورة المراجعة (مثلاً سكرين شوت من واتساب)</label>
                <div className="flex items-center gap-4">
                  <div className="w-24 h-24 bg-slate-100 rounded-xl border border-slate-300 flex items-center justify-center overflow-hidden flex-shrink-0">
                    {formData.content ? (
                      <img src={formData.content} alt="review" className="w-full h-full object-cover" />
                    ) : (
                      <SafeIcon icon={LuImage} className="w-8 h-8 text-slate-300" />
                    )}
                  </div>
                  <div className="flex-1 space-y-2">
                    <input type="file" accept="image/*" ref={imageRef} onChange={(e) => handleUpload(e, 'image')} className="hidden" />
                    <button type="button" onClick={() => imageRef.current?.click()} disabled={uploadingImage} className="flex items-center gap-2 px-4 py-2 bg-indigo-100 text-indigo-700 hover:bg-indigo-200 rounded-lg text-sm font-bold transition-colors">
                      <SafeIcon icon={uploadingImage ? LuX : LuUpload} className="w-4 h-4" />
                      {uploadingImage ? 'جاري الرفع...' : 'رفع صورة'}
                    </button>
                    <input required type="url" value={formData.content} onChange={e => setFormData({ ...formData, content: e.target.value })} placeholder="أو ضع رابط الصورة هنا" dir="ltr" className="w-full p-2 border border-slate-200 rounded-lg text-xs outline-none text-left" />
                  </div>
                </div>
              </div>
            )}
          </div>

          <button type="submit" className="bg-indigo-600 hover:bg-indigo-700 text-white px-6 py-2.5 rounded-lg font-medium transition-colors flex items-center gap-2">
            <SafeIcon icon={LuSave} className="w-4 h-4" />
            {editingId ? 'حفظ التعديلات' : 'إضافة المراجعة'}
          </button>
        </form>
      )}

      {loading ? (
        <div className="text-center py-10 text-slate-500">جاري التحميل...</div>
      ) : (
        <div className="space-y-3">
          {reviews.map(review => (
            <div key={review.id} className="flex items-center justify-between p-4 rounded-xl border border-slate-200 bg-slate-50">
              <div className="flex gap-4 items-center flex-1">
                <div className="w-12 h-12 bg-white border border-slate-200 flex items-center justify-center rounded-xl flex-shrink-0 overflow-hidden">
                  {review.review_type === 'text' && review.avatar_url ? (
                    <img src={review.avatar_url} className="w-full h-full object-cover" />
                  ) : review.review_type === 'image' && review.content ? (
                    <img src={review.content} className="w-full h-full object-cover" />
                  ) : (
                    <SafeIcon icon={REVIEW_TYPES.find(t => t.id === (review.review_type || 'video'))?.icon || LuVideo} className="w-6 h-6 text-indigo-500" />
                  )}
                </div>
                <div>
                  <h3 className="font-bold text-slate-800">{review.customer_name}</h3>
                  <div className="flex flex-wrap items-center gap-2 mt-1">
                    <span className="text-[10px] sm:text-xs font-bold bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-md border border-indigo-200">
                      مجموعة: {review.section_title || 'آراء زبائننا'}
                    </span>
                    <span className="text-[10px] sm:text-xs bg-slate-200 text-slate-600 px-2 py-0.5 rounded-md">
                      {review.location === 'home_bottom' ? 'الأسفل' : review.location === 'none' ? 'مخفي' : 'فوق السيارات'}
                    </span>
                    <span className="text-[10px] sm:text-xs text-slate-500">{review.car_purchased}</span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-shrink-0">
                <button onClick={() => toggleStatus(review)} className={`px-3 py-1.5 rounded-lg text-xs font-medium hidden sm:block ${review.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-700'}`}>
                  {review.is_active ? 'نشط' : 'مخفي'}
                </button>
                <button onClick={() => startEdit(review)} className="p-2 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors">
                  <SafeIcon icon={LuEdit} className="w-4 h-4" />
                </button>
                <button onClick={() => handleDelete(review.id)} className="p-2 text-red-600 hover:bg-red-100 rounded-lg transition-colors">
                  <SafeIcon icon={LuTrash2} className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
          {reviews.length === 0 && !isAdding && <p className="text-center text-slate-500 py-6">لا توجد مراجعات.</p>}
        </div>
      )}
    </div>
  );
};

export default AdminReviews;